import {
  FormErrors,
  PageLoading,
  useDataProvider,
  useMasterData,
  useUserAccess,
} from '@ui-framework/index';
import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Engine } from 'json-rules-engine';
import React, {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { ASSIGNED_SP_DISPATCH_METHOD } from '@constants/servicejob';
import { useServiceJob } from '../ServiceJobProvider';
import SERVICE_JOB_DISPATCH_BUTTON from '../../../config/formSchemaButtons/SERVICE_JOB_DISPATCH_BUTTON.json';
import RESCHEDULE_BUTTON from '../../../config/formSchemaButtons/RESCHEDULE_BUTTON.json';

const ServiceJobDispatchOptionsContext = createContext({});

export function ServiceJobDispatchAndRescheduleOptionsProvider({
  children,
  isRescheduleFlow = false,
}) {
  const userAccess = useUserAccess();
  const masterData = useMasterData();
  const { serviceJob } = useServiceJob();

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState(null);
  const [dispatchOptions, setDispatchOptions] = useState(null);

  const hasDispatchAccessRule = SERVICE_JOB_DISPATCH_BUTTON.visibility.rules;
  const RescheduleButtonStatuses = masterData?.reschedulableJobStatuses || [];

  const hasRescheduleAccessRule = RESCHEDULE_BUTTON.visibility.rules;

  const dispatchNotAllowedMessage = (
    <FormattedMessage
      id="DISPATCH_NOT_ALLOWED_FOR_THIS_JOB"
      defaultMessage="Dispatch not allowed for this job"
    />
  );

  const rescheduleNotAllowedMessage = (
    <FormattedMessage
      id="RESCHEDULE_NOT_ALLOWED_FOR_THIS_JOB"
      defaultMessage="Reschedule not allowed for this job"
    />
  );

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl } = useDataProvider(provider);

  const runRuleEngine = async ({ rules, params = {} }) => {
    const engine = new Engine();
    // eslint-disable-next-line no-restricted-syntax
    for (const rule of rules) {
      // eslint-disable-next-line no-await-in-loop
      await engine.addRule(rule);
    }
    try {
      const result = await engine.run(params);
      return result;
    } catch (e) {
      return e.message;
    }
  };

  const getJobDispatchOptions = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJob?.id}/dispatch-options`,
    );
    return response;
  };

  const getSPDispatchDetails = async () => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-provider-profile/${serviceJob?.serviceProviderId}/dispatch`,
    );
    return response;
  };

  const isDispatchRulePassed = async () => {
    let rulePassed = false;
    const result = await runRuleEngine({
      rules: hasDispatchAccessRule,
      params: {
        ...serviceJob,
        roleIdentifier: userAccess?.role?.roleIdentifier,
        companyType: userAccess.company.companyType,
        RescheduleButtonStatuses,
      },
    });

    if (Array.isArray(result) && result.length > 0) {
      const ruleEngineResult = result?.[0];
      if (ruleEngineResult.type === 'RETURN_TRUE') {
        rulePassed = true;
      }
    }
    return rulePassed;
  };

  const isRescheduleRulePassed = async () => {
    let rulePassed = false;
    const result = await runRuleEngine({
      rules: hasRescheduleAccessRule,
      params: {
        ...serviceJob,
        roleIdentifier: userAccess?.role?.roleIdentifier,
        companyType: userAccess.company.companyType,
        RescheduleButtonStatuses,
      },
    });

    if (Array.isArray(result) && result.length > 0) {
      const ruleEngineResult = result?.[0];
      if (ruleEngineResult.type === 'RETURN_TRUE') {
        rulePassed = true;
      }
    }
    return rulePassed;
  };

  const DISPATCH_METHOD_ASSIGNED_SERVICE_PROVIDER_LOGGED_IN =
    'DISPATCH_METHOD_ASSIGNED_SERVICE_PROVIDER_LOGGED_IN';

  const initialize = async () => {
    setLoading(true);
    setErrors(null);
    let jobDispatchOptionsFound = [];
    let errorsFound = [];
    let rulePassed = false;
    try {
      if (isRescheduleFlow) {
        rulePassed = isRescheduleRulePassed();
        if (!rulePassed) {
          errorsFound = [{ message: rescheduleNotAllowedMessage }];
        }
      } else {
        rulePassed = isDispatchRulePassed();
        if (!rulePassed) {
          errorsFound = [{ message: dispatchNotAllowedMessage }];
        }
      }

      if (rulePassed) {
        // If SP is rescheduling then show only his dispatch method
        if (isRescheduleFlow) {
          // TODO: Check if the dispatch method is active

          // If current user is a SP rescheduling a job
          if (userAccess.isSP()) {
            // For SP flow it will always be single date entry
            jobDispatchOptionsFound = [
              ...jobDispatchOptionsFound,
              {
                dispatchMethod:
                  DISPATCH_METHOD_ASSIGNED_SERVICE_PROVIDER_LOGGED_IN,
                score: 0,
              },
            ];
          } else if (userAccess.isSA() || userAccess.isAdmin()) {
            // If there is a assigned SP
            if (serviceJob?.serviceProviderId) {
              const spDispatchDetailsResponse = await getSPDispatchDetails();
              if (spDispatchDetailsResponse?.ok) {
                const dispatchMethod =
                  spDispatchDetailsResponse?.data?.dispatchMethod;
                jobDispatchOptionsFound = [
                  ...jobDispatchOptionsFound,
                  {
                    dispatchMethod: `${ASSIGNED_SP_DISPATCH_METHOD}_${dispatchMethod}`,
                    score: 0,
                  },
                ];
              } else {
                errorsFound = [
                  ...errorsFound,
                  ...spDispatchDetailsResponse.errors,
                ];
              }
            }
          }
        }

        if (userAccess.isSA() || userAccess.isAdmin()) {
          const jobDispatchOptionsResponse = await getJobDispatchOptions();
          if (jobDispatchOptionsResponse?.ok) {
            jobDispatchOptionsFound = [
              ...jobDispatchOptionsFound,
              ...(jobDispatchOptionsResponse?.data?.routingOptions || []),
            ];
          } else {
            errorsFound = [
              ...errorsFound,
              ...jobDispatchOptionsResponse.errors,
            ];
          }
        }
      }
    } catch (err) {
      errorsFound = [{ message: err.message }];
    }

    setDispatchOptions(jobDispatchOptionsFound);
    setErrors(errorsFound);
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  const value = useMemo(
    () => ({
      dispatchOptions,
      DISPATCH_METHOD_ASSIGNED_SERVICE_PROVIDER_LOGGED_IN,
    }),
    [dispatchOptions],
  );

  if (loading) {
    return (
      <PageLoading>
        <FormattedMessage
          id="LOADING_SERVICE_JOB_DISPATCH_OPTIONS"
          defaultMessage="Loading Service Job Dispatch Options"
        />
        ...
      </PageLoading>
    );
  }

  return (
    <ServiceJobDispatchOptionsContext.Provider value={value}>
      {Array(errors) && errors.length > 0 ? (
        <FormErrors errors={errors} />
      ) : (
        <Box>{children}</Box>
      )}
    </ServiceJobDispatchOptionsContext.Provider>
  );
}

export const useServiceJobDispatchAndRescheduleOptions = () => {
  return useContext(ServiceJobDispatchOptionsContext);
};
